import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/movies" },
  { label: "电视剧", to: "/tvshows" },
  { label: "影视资讯", to: "/news" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const { favorites } = useFavorites();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const query = keyword.trim();
    setMobileOpen(false);
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-3 py-2 text-sm font-label transition-colors duration-200 ${
      isActive
        ? "bg-primary-500/15 text-primary-400"
        : "text-foreground-700 hover:bg-background-200/60 hover:text-foreground-950"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
      <div className="flex h-16 w-full items-center gap-3 px-4 md:px-6 lg:h-[72px] lg:px-10">
        <Link to="/" className="flex items-center gap-2.5" aria-label="夜无疆影视大全首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-moon-fill text-lg" />
          </span>
          <span className="font-heading text-lg font-bold tracking-wide text-foreground-950 lg:text-xl">
            夜无疆
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="hidden h-10 w-56 items-center gap-2 rounded-full border border-background-300/70 bg-background-100/80 px-4 transition-colors duration-200 focus-within:border-primary-500/60 md:flex xl:w-72"
          >
            <i className="ri-search-line text-sm text-foreground-500" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索电影、电视剧、资讯"
              className="h-full w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
              aria-label="站内搜索"
            />
          </form>

          <Link
            to="/search"
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors duration-200 hover:bg-background-200/60 hover:text-foreground-950 md:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg" />
          </Link>

          <Link
            to="/favorites"
            className="relative flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors duration-200 hover:bg-background-200/60 hover:text-foreground-950"
            aria-label="我的收藏"
          >
            <i className="ri-heart-3-line text-lg" />
            {favorites.length > 0 && (
              <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-background-50">
                {favorites.length}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((value) => !value)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors duration-200 hover:bg-background-200/60 hover:text-foreground-950 lg:hidden"
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200/70 bg-background-50 lg:hidden">
          <div className="space-y-3 px-4 py-4">
            <form
              onSubmit={submitSearch}
              className="flex h-11 items-center gap-2 rounded-full border border-background-300/70 bg-background-100/80 px-4 focus-within:border-primary-500/60"
            >
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索电影、电视剧、资讯"
                className="h-full w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
                aria-label="站内搜索"
              />
            </form>
            <nav className="grid gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `rounded-md px-4 py-3 text-sm font-label transition-colors duration-200 ${
                      isActive
                        ? "bg-primary-500/15 text-primary-400"
                        : "text-foreground-700 hover:bg-background-200/60"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <NavLink
                to="/favorites"
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-4 py-3 text-sm font-label text-foreground-700 transition-colors duration-200 hover:bg-background-200/60"
              >
                我的收藏
              </NavLink>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}